import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import FormSmallTextField from "../../../../../../views/components/common/form/FormTextField";
import DeleteConfirmation from "../../../../../../views/components/common/pop-up/DeleteConfirmation";
import { useStores } from "../../../../use-stores";
import type {
  ProductComment,
  ProductCommentAttachment,
  ProductCommentHistoryEntry,
} from "../../../../api/ProjectProductCommentsAPI";
import ImageAPI from "../../../../api/ImageAPI";
import { HelperAPI } from "../../../../api/HelperAPI";
import { useNotification } from "../../../../../../utils/NotificationsContext";

interface ProjectProductCommentsProps {
  projectId: number;
  productId: number;
  archived: boolean;
  open?: boolean;
  hideTitle?: boolean;
}

const AVATAR_COLORS = [
  "#5E35B1",
  "#1E88E5",
  "#00897B",
  "#E53935",
  "#FB8C00",
  "#8E24AA",
  "#3949AB",
  "#00ACC1",
];

const MAX_ATTACHMENTS = 10;
const MAX_TOTAL_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ATTACHMENT_CONTAINER = "product-comments";
const COMMENT_FONT_SIZE = "0.7rem";
const COMMENT_META_FONT_SIZE = "0.58rem";
const COMMENT_NAME_FONT_SIZE = "0.68rem";
const COMMENT_AVATAR_SIZE = 22;
const COMMENT_MAX_LENGTH = 2000;

const getInitials = (name?: string | null): string => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (
    parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
  ).toUpperCase();
};

const avatarColor = (name?: string | null): string => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const formatCommentTime = (value?: string | null): string => {
  if (!value) return "";
  const date = dayjs(value);
  if (!date.isValid()) return "";

  const diffMin = dayjs().diff(date, "minute");
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? "" : "s"} ago`;
  const diffHr = dayjs().diff(date, "hour");
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  const diffDay = dayjs().diff(date, "day");
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;

  return date.format("DD MMM YYYY, HH:mm");
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getAttachmentName = (attachment: ProductCommentAttachment): string =>
  attachment.file_name ||
  attachment.file_path.split("/").pop() ||
  "attachment";

const flattenComments = (nodes: ProductComment[]): ProductComment[] => {
  const flat: ProductComment[] = [];
  const walk = (list: ProductComment[]) => {
    for (const node of list) {
      flat.push(node);
      if (node.replies?.length) walk(node.replies);
    }
  };
  walk(nodes);
  return flat.sort(
    (a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf(),
  );
};

const getQuotePreview = (parent?: ProductComment | null): string => {
  if (!parent) return "Original message";
  if (parent.deleted_at) return "This comment was deleted";
  const text = (parent.body || "").trim();
  if (!text) {
    const count = parent.attachments?.length || 0;
    if (count > 0) return `${count} attachment${count === 1 ? "" : "s"}`;
    return "Original message";
  }
  return text.length > 120 ? `${text.slice(0, 120)}…` : text;
};

const isImageAttachment = (attachment: ProductCommentAttachment): boolean => {
  const name = getAttachmentName(attachment).toLowerCase();
  return (
    /\.(png|jpe?g|webp|gif)$/i.test(name) ||
    /\.(png|jpe?g|webp|gif)(\?|$)/i.test(attachment.file_path)
  );
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

const CommentAttachmentItem: React.FC<{
  attachment: ProductCommentAttachment;
  compact?: boolean;
}> = ({ attachment, compact = false }) => {
  const theme = useTheme();
  const { showError } = useNotification();
  const name = getAttachmentName(attachment);
  const showImage = isImageAttachment(attachment);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(showImage);

  useEffect(() => {
    if (!showImage || !attachment.file_path) {
      setIsPreviewLoading(false);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    const loadPreview = async () => {
      try {
        setIsPreviewLoading(true);
        const response = await HelperAPI.downloadAFile(attachment.file_path);
        if (cancelled || !response?.data) return;
        objectUrl = window.URL.createObjectURL(response.data);
        setPreviewUrl(objectUrl);
      } catch (error) {
        console.error("Failed to load comment attachment preview:", error);
        if (!cancelled) {
          setPreviewUrl(null);
        }
      } finally {
        if (!cancelled) {
          setIsPreviewLoading(false);
        }
      }
    };

    void loadPreview();

    return () => {
      cancelled = true;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [attachment.file_path, showImage]);

  const handleDownload = async () => {
    if (isDownloading || !attachment.file_path) return;

    try {
      setIsDownloading(true);
      const response = await HelperAPI.downloadAFile(attachment.file_path);
      if (!response?.data) {
        throw new Error("Failed to download attachment.");
      }
      triggerBlobDownload(response.data, response.filename || name);
    } catch (error: any) {
      console.error("Failed to download comment attachment:", error);
      showError(
        "Error Occurred",
        error?.response?.data?.message ||
          error?.message ||
          "Failed to download attachment. Please try again.",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box
      component="button"
      type="button"
      onClick={() => {
        void handleDownload();
      }}
      disabled={isDownloading}
      title={`Download ${name}`}
      sx={{
        display: "inline-flex",
        flexDirection: showImage ? "column" : "row",
        alignItems: showImage ? "stretch" : "center",
        gap: 0.75,
        maxWidth: "100%",
        px: 1,
        py: 0.4,
        borderRadius: 1.5,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.common.black, 0.03),
        textDecoration: "none",
        color: theme.palette.text.primary,
        cursor: isDownloading ? "wait" : "pointer",
        textAlign: "left",
        font: "inherit",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
          color: theme.palette.primary.main,
        },
        "&:disabled": {
          opacity: 0.7,
        },
      }}
    >
      {showImage ? (
        isPreviewLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: compact ? 80 : 120,
            }}
          >
            <CircularProgress size={20} />
          </Box>
        ) : previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt={name}
            sx={{
              width: "100%",
              maxHeight: compact ? 140 : 220,
              objectFit: "contain",
              borderRadius: 1,
              backgroundColor: theme.palette.common.white,
            }}
          />
        ) : (
          <InsertDriveFileOutlinedIcon
            sx={{
              fontSize: 16,
              color: theme.palette.customColors.greyTextColor,
              flexShrink: 0,
              alignSelf: "center",
              my: 1,
            }}
          />
        )
      ) : (
        <InsertDriveFileOutlinedIcon
          sx={{
            fontSize: 16,
            color: theme.palette.customColors.greyTextColor,
            flexShrink: 0,
          }}
        />
      )}
      <Box
        display="flex"
        alignItems="center"
        gap={0.75}
        minWidth={0}
        width="100%"
      >
        <CustomTypography
          variant="heading8"
          sx={{
            wordBreak: "break-all",
            fontSize: COMMENT_FONT_SIZE,
            flex: 1,
          }}
        >
          {name}
        </CustomTypography>
        {isDownloading ? (
          <CircularProgress size={14} sx={{ flexShrink: 0 }} />
        ) : (
          <FileDownloadOutlinedIcon sx={{ fontSize: 16, flexShrink: 0 }} />
        )}
      </Box>
    </Box>
  );
};

const CommentAttachmentsList: React.FC<{
  attachments?: ProductCommentAttachment[];
  compact?: boolean;
}> = ({ attachments, compact = false }) => {
  if (!attachments || attachments.length === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        mt: compact ? 0.75 : 1,
      }}
    >
      {attachments.map((att) => (
        <CommentAttachmentItem
          key={att.id || att.file_path}
          attachment={att}
          compact={compact}
        />
      ))}
    </Box>
  );
};

/** WhatsApp-style quoted parent preview inside a bubble */
const ReplyQuote: React.FC<{
  parent?: ProductComment | null;
  accent: string;
  onClick?: () => void;
}> = ({ parent, accent, onClick }) => {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        mb: 0.75,
        borderRadius: 1.5,
        overflow: "hidden",
        cursor: onClick ? "pointer" : "default",
        backgroundColor: alpha(theme.palette.common.black, 0.05),
      }}
    >
      <Box sx={{ width: 3.5, flexShrink: 0, backgroundColor: accent }} />
      <Box sx={{ px: 1, py: 0.6, minWidth: 0, flex: 1 }}>
        <CustomTypography
          variant="heading8"
          fontWeight={700}
          sx={{ color: accent, lineHeight: 1.3, fontSize: COMMENT_NAME_FONT_SIZE }}
        >
          {parent?.author_name || "Unknown"}
        </CustomTypography>
        <CustomTypography
          variant="heading8"
          color={theme.palette.customColors.greyTextColor}
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.35,
            fontSize: COMMENT_FONT_SIZE,
            fontStyle: parent?.deleted_at ? "italic" : "normal",
          }}
        >
          {getQuotePreview(parent)}
        </CustomTypography>
      </Box>
    </Box>
  );
};

const ProjectProductComments: React.FC<ProjectProductCommentsProps> = observer(
  ({ projectId, productId, archived, open = true, hideTitle = false }) => {
    const theme = useTheme();
    const { projectSourcingOpsStore, userStore } = useStores();
    const [draft, setDraft] = useState("");
    const [pendingFiles, setPendingFiles] = useState<File[]>([]);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [replyingToId, setReplyingToId] = useState<number | null>(null);
    const [expandedHistoryIds, setExpandedHistoryIds] = useState<Set<number>>(
      new Set(),
    );
    const [highlightId, setHighlightId] = useState<number | null>(null);
    const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [composerError, setComposerError] = useState<string | null>(null);
    const listEndRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const draftInputRef = useRef<HTMLTextAreaElement | null>(null);
    const highlightTimeoutRef = useRef<number | null>(null);
    const commentRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());

    const isBusy =
      isSubmitting || projectSourcingOpsStore.savingProductComment;
    const canType = !archived;
    const commentsTree = projectSourcingOpsStore.productComments;
    const currentUserId = userStore.user?.id;
    const brand = theme.palette.customColors.purple || "#6455ED";

    const flatComments = useMemo(
      () => flattenComments(commentsTree),
      [commentsTree],
    );

    const commentsById = useMemo(() => {
      const map = new Map<number, ProductComment>();
      flatComments.forEach((comment) => map.set(comment.id, comment));
      return map;
    }, [flatComments]);

    const replyingTo = replyingToId
      ? commentsById.get(replyingToId) || null
      : null;
    const editingComment = editingId
      ? commentsById.get(editingId) || null
      : null;

    const pendingTotalBytes = pendingFiles.reduce(
      (sum, file) => sum + file.size,
      0,
    );
    const canSend =
      canType &&
      !isBusy &&
      pendingTotalBytes <= MAX_TOTAL_ATTACHMENT_BYTES &&
      (editingId != null
        ? draft.trim().length > 0
        : draft.trim().length > 0 || pendingFiles.length > 0);

    const isDeletedComment = (comment: ProductComment): boolean =>
      Boolean(comment.deleted_at);

    const isOwnComment = (comment: ProductComment): boolean => {
      if (currentUserId == null) return false;
      return Number(comment.created_by) === Number(currentUserId);
    };

    const canEditComment = (comment: ProductComment): boolean =>
      canType &&
      !isDeletedComment(comment) &&
      isOwnComment(comment) &&
      (comment.can_edit === true || comment.can_edit == null);

    const canDeleteComment = (comment: ProductComment): boolean =>
      canType &&
      !isDeletedComment(comment) &&
      isOwnComment(comment) &&
      (comment.can_delete === true || comment.can_delete == null);

    const scrollToBottom = () => {
      requestAnimationFrame(() => {
        listEndRef.current?.scrollIntoView({ behavior: "smooth" });
      });
    };

    const scrollToComment = (commentId: number) => {
      const el = commentRefs.current.get(commentId);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightId(commentId);
      if (highlightTimeoutRef.current != null) {
        window.clearTimeout(highlightTimeoutRef.current);
      }
      highlightTimeoutRef.current = window.setTimeout(() => {
        setHighlightId(null);
        highlightTimeoutRef.current = null;
      }, 1600);
    };

    useEffect(() => {
      return () => {
        if (highlightTimeoutRef.current != null) {
          window.clearTimeout(highlightTimeoutRef.current);
        }
      };
    }, []);

    useEffect(() => {
      if (!open || !projectId || !productId) return;
      setDraft("");
      setPendingFiles([]);
      setEditingId(null);
      setReplyingToId(null);
      setExpandedHistoryIds(new Set());
      setComposerError(null);
      setIsSubmitting(false);

      const openComments = async () => {
        try {
          await projectSourcingOpsStore.markProductCommentsAsRead(
            projectId,
            productId,
          );
        } catch {
          // best-effort
        }

        try {
          await projectSourcingOpsStore.loadProductComments(
            projectId,
            productId,
          );
          scrollToBottom();
        } catch (error: any) {
          setComposerError(
            error?.response?.data?.message ||
              error?.message ||
              "Failed to load comments",
          );
        }
      };

      void openComments();
    }, [open, projectId, productId]);

    useEffect(() => {
      if (!open) return;
      scrollToBottom();
    }, [open, flatComments.length]);

    if (!open) return null;

    const handleFilesSelected = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const selected = event.target.files
        ? Array.from(event.target.files)
        : [];
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (selected.length === 0) return;

      const filesOnly = selected.filter((file) => {
        const name = (file.name || "").trim();
        const relativePath = (file as File & { webkitRelativePath?: string })
          .webkitRelativePath;
        const looksLikeFolder =
          !name ||
          name.endsWith("/") ||
          name.endsWith("\\") ||
          Boolean(relativePath && relativePath.includes("/"));
        return !looksLikeFolder;
      });

      if (filesOnly.length === 0) {
        setComposerError("Folders are not allowed. Please select files only.");
        return;
      }

      if (filesOnly.length < selected.length) {
        setComposerError("Folders were ignored. Only files were added.");
      }

      const remainingSlots = MAX_ATTACHMENTS - pendingFiles.length;
      if (remainingSlots <= 0) {
        setComposerError(`You can attach at most ${MAX_ATTACHMENTS} files.`);
        return;
      }

      const acceptedFiles = filesOnly.slice(0, remainingSlots);
      if (acceptedFiles.length < filesOnly.length) {
        setComposerError(
          `Only ${MAX_ATTACHMENTS} files are allowed. Extra files were skipped.`,
        );
      }

      const nextFiles = [...pendingFiles, ...acceptedFiles];
      const totalBytes = nextFiles.reduce((sum, file) => sum + file.size, 0);
      if (totalBytes > MAX_TOTAL_ATTACHMENT_BYTES) {
        setComposerError(
          `Total attachment size must be at most ${formatBytes(MAX_TOTAL_ATTACHMENT_BYTES)}.`,
        );
        return;
      }
      setPendingFiles(nextFiles);
      if (acceptedFiles.length === filesOnly.length) {
        setComposerError(null);
      }
    };

    const focusComposer = () => {
      requestAnimationFrame(() => {
        const el = draftInputRef.current;
        if (!el || el.disabled) return;
        try {
          el.focus({ preventScroll: true });
        } catch {
          el.focus();
        }
      });
    };

    const uploadAndCreate = async (
      body: string,
      files: File[],
      parentId: number | null,
    ) => {
      let attachments: Array<{ file_path: string; file_name?: string }> = [];
      if (files.length > 0) {
        const urls = await new ImageAPI().uploadCommentAttachments(
          files,
          ATTACHMENT_CONTAINER,
        );
        attachments = urls.map((file_path, index) => ({
          file_path,
          file_name: files[index]?.name,
        }));
      }

      await projectSourcingOpsStore.createProductComment(
        projectId,
        productId,
        body,
        attachments,
        parentId,
      );
    };

    const handleSend = async () => {
      const body = draft.trim();
      const hasFiles = pendingFiles.length > 0;
      if (!canSend || isBusy) return;
      if (editingId != null) {
        if (!body) return;
      } else if (!body && !hasFiles) {
        return;
      }

      setIsSubmitting(true);
      setComposerError(null);
      try {
        if (editingId != null) {
          await projectSourcingOpsStore.updateProductComment(
            projectId,
            productId,
            editingId,
            body,
          );
          setDraft("");
          setPendingFiles([]);
          setEditingId(null);
          return;
        }

        await uploadAndCreate(body, pendingFiles, replyingToId);
        setDraft("");
        setPendingFiles([]);
        setReplyingToId(null);
      } catch (error: any) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          (editingId != null
            ? "Failed to update comment"
            : "Failed to add comment");
        setComposerError(message);
      } finally {
        setIsSubmitting(false);
        focusComposer();
      }
    };

    const startReply = (comment: ProductComment) => {
      if (!canType || isDeletedComment(comment)) return;
      setEditingId(null);
      setReplyingToId(comment.id);
    };

    const cancelReply = () => {
      setReplyingToId(null);
    };

    const startEdit = (comment: ProductComment) => {
      if (!canEditComment(comment)) {
        setComposerError("You can only edit your own comments.");
        return;
      }
      setReplyingToId(null);
      setPendingFiles([]);
      setEditingId(comment.id);
      setDraft(comment.body || "");
      scrollToComment(comment.id);
    };

    const cancelEdit = () => {
      setEditingId(null);
      setDraft("");
      setPendingFiles([]);
    };

    const handleDelete = (comment: ProductComment) => {
      if (!canDeleteComment(comment) || isBusy) return;
      setDeleteTargetId(comment.id);
    };

    const handleConfirmDelete = async (commentId: number) => {
      if (!commentId || isBusy) return;
      setDeleteTargetId(null);
      try {
        await projectSourcingOpsStore.deleteProductComment(
          projectId,
          productId,
          commentId,
        );
        if (editingId === commentId) cancelEdit();
        if (replyingToId === commentId) cancelReply();
      } catch (error: any) {
        setComposerError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to delete comment",
        );
      }
    };

    const toggleHistory = async (comment: ProductComment) => {
      if (!isOwnComment(comment)) {
        setComposerError("You can only view history for your own comments.");
        return;
      }

      const next = new Set(expandedHistoryIds);
      if (next.has(comment.id)) {
        next.delete(comment.id);
        setExpandedHistoryIds(next);
        return;
      }
      next.add(comment.id);
      setExpandedHistoryIds(next);

      try {
        await projectSourcingOpsStore.loadProductCommentHistory(
          projectId,
          productId,
          comment.id,
        );
      } catch (error: any) {
        next.delete(comment.id);
        setExpandedHistoryIds(new Set(next));
        setComposerError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to load comment history",
        );
      }
    };

    const formatHistoryBody = (
      entry: ProductCommentHistoryEntry,
    ): string | null => {
      if (entry.action === "created") {
        return entry.body_after ? `"${entry.body_after}"` : null;
      }
      if (entry.action === "deleted") {
        return entry.body_before ? `"${entry.body_before}"` : null;
      }
      if (entry.body_before === entry.body_after) return null;
      return `"${entry.body_before ?? ""}" → "${entry.body_after ?? ""}"`;
    };

    const renderHistoryRows = (entries: ProductCommentHistoryEntry[]) => {
      if (entries.length === 0) {
        return (
          <CustomTypography
            variant="heading8"
            color={theme.palette.customColors.greyTextColor}
            style={{ fontSize: COMMENT_FONT_SIZE }}
          >
            No edit history yet.
          </CustomTypography>
        );
      }

      return entries.map((entry, index) => {
        const label =
          entry.action === "created"
            ? "Created"
            : entry.action === "updated"
              ? "Edited"
              : "Deleted";
        const bodyText = formatHistoryBody(entry);
        return (
          <Box
            key={entry.id}
            sx={{
              pb: index === entries.length - 1 ? 0 : 1,
              mb: index === entries.length - 1 ? 0 : 1,
              borderBottom:
                index === entries.length - 1
                  ? "none"
                  : `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
            >
              <CustomTypography
                variant="heading8"
                fontWeight={700}
                style={{ fontSize: COMMENT_NAME_FONT_SIZE }}
              >
                {label}
              </CustomTypography>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.greyTextColor}
                style={{
                  fontSize: COMMENT_META_FONT_SIZE,
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                {formatCommentTime(entry.created_at)}
              </CustomTypography>
            </Box>
            {bodyText && (
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.greyTextColor}
                style={{
                  fontSize: COMMENT_FONT_SIZE,
                  marginTop: 4,
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.35,
                }}
              >
                {bodyText}
              </CustomTypography>
            )}
          </Box>
        );
      });
    };

    const renderMessage = (comment: ProductComment) => {
      const isOwn = isOwnComment(comment);
      const isDeleted = isDeletedComment(comment);
      const isEditing = editingId === comment.id;
      const showEdit = canEditComment(comment);
      const showDelete = canDeleteComment(comment);
      const canViewHistory = isOwn;
      const historyExpanded =
        canViewHistory && expandedHistoryIds.has(comment.id);
      const historyEntries =
        projectSourcingOpsStore.productCommentHistoryById.get(comment.id) ||
        [];
      const parent = comment.parent_id
        ? commentsById.get(comment.parent_id)
        : null;
      const accent = avatarColor(parent?.author_name || comment.author_name);
      const wasEdited =
        !isDeleted &&
        comment.updated_at &&
        comment.created_at &&
        dayjs(comment.updated_at).valueOf() !==
          dayjs(comment.created_at).valueOf();
      const isHighlighted = highlightId === comment.id || isEditing;

      const bubbleBg = isOwn
        ? alpha(brand, 0.16)
        : theme.palette.grey[100];
      const bubbleBorder = isOwn
        ? alpha(brand, 0.22)
        : theme.palette.divider;

      const hasActions =
        (canType && !isEditing && !isDeleted) ||
        canViewHistory ||
        showEdit ||
        showDelete;
      const forceShowActions = isEditing || historyExpanded || isHighlighted;

      const actionButtons = hasActions ? (
        <Box
          className="comment-actions"
          display="flex"
          alignItems="center"
          gap={0.15}
          sx={{
            position: "absolute",
            top: 2,
            zIndex: 1,
            ...(isOwn
              ? { right: "100%", mr: 0.5 }
              : { left: "100%", ml: 0.5 }),
            borderRadius: 1.5,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.08)}`,
            px: 0.15,
            py: 0.1,
            opacity: forceShowActions ? 1 : { xs: 1, md: 0 },
            pointerEvents: forceShowActions
              ? "auto"
              : { xs: "auto", md: "none" },
            transition: "opacity .15s ease",
            whiteSpace: "nowrap",
          }}
        >
          {canType && !isEditing && !isDeleted && (
            <Tooltip title="Reply">
              <IconButton
                size="small"
                onClick={() => startReply(comment)}
                sx={{ width: 26, height: 26 }}
              >
                <ReplyRoundedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          {canViewHistory && (
            <Tooltip
              title={
                historyExpanded ? "Hide edit history" : "Show edit history"
              }
            >
              <IconButton
                size="small"
                onClick={() => void toggleHistory(comment)}
                sx={{
                  width: 26,
                  height: 26,
                  color: historyExpanded ? brand : "inherit",
                  backgroundColor: historyExpanded
                    ? alpha(brand, 0.12)
                    : "transparent",
                }}
              >
                {historyExpanded ? (
                  <KeyboardArrowUpRoundedIcon sx={{ fontSize: 14 }} />
                ) : (
                  <HistoryOutlinedIcon sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </Tooltip>
          )}
          {showEdit && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => startEdit(comment)}
                sx={{ width: 26, height: 26 }}
              >
                <EditOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          {showDelete && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                disabled={isBusy}
                onClick={() => handleDelete(comment)}
                sx={{
                  width: 26,
                  height: 26,
                  backgroundColor: theme.palette.customColors.pinkLight,
                }}
              >
                <DeleteIcon
                  sx={{
                    fontSize: 13,
                    color: theme.palette.customColors.pinkDark,
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ) : null;

      return (
        <Box
          key={comment.id}
          ref={(el: HTMLDivElement | null) => {
            commentRefs.current.set(comment.id, el);
          }}
          display="flex"
          justifyContent={isOwn ? "flex-end" : "flex-start"}
          alignItems="flex-end"
          gap={1}
          mb={1.25}
          sx={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            transition: "background-color .25s ease",
            borderRadius: 2,
            px: 0.25,
            py: 0.25,
            backgroundColor: isHighlighted
              ? alpha(brand, 0.1)
              : "transparent",
            "&:hover .comment-actions": {
              opacity: 1,
              pointerEvents: "auto",
              transition: "opacity .15s ease 0.12s",
            },
          }}
        >
          {!isOwn && (
            <Avatar
              sx={{
                width: COMMENT_AVATAR_SIZE,
                height: COMMENT_AVATAR_SIZE,
                fontSize: "0.55rem",
                fontWeight: 600,
                bgcolor: isDeleted
                  ? theme.palette.grey[400]
                  : avatarColor(comment.author_name),
                flexShrink: 0,
              }}
            >
              {getInitials(comment.author_name)}
            </Avatar>
          )}

          <Box
            sx={{
              position: "relative",
              maxWidth: hasActions
                ? `min(70%, calc(100% - ${COMMENT_AVATAR_SIZE + 120}px))`
                : `min(70%, calc(100% - ${COMMENT_AVATAR_SIZE + 16}px))`,
              width: "fit-content",
              minWidth: 0,
            }}
          >
            {actionButtons}
            <Box
              sx={{
                px: 1.35,
                py: 1,
                borderRadius: isOwn
                  ? "12px 4px 12px 12px"
                  : "4px 12px 12px 12px",
                backgroundColor: bubbleBg,
                border: `1px solid ${bubbleBorder}`,
                boxShadow: `0 1px 1px ${alpha(theme.palette.common.black, 0.04)}`,
              }}
            >
              {!isOwn && (
                <CustomTypography
                  variant="heading8"
                  fontWeight={700}
                  sx={{
                    color: accent,
                    mb: 0.35,
                    lineHeight: 1.2,
                    fontSize: COMMENT_NAME_FONT_SIZE,
                  }}
                >
                  {comment.author_name || "Unknown"}
                </CustomTypography>
              )}

              {parent && (
                <ReplyQuote
                  parent={parent}
                  accent={avatarColor(parent.author_name)}
                  onClick={() => scrollToComment(parent.id)}
                />
              )}

              {isDeleted ? (
                <CustomTypography
                  variant="heading8"
                  sx={{
                    fontStyle: "italic",
                    color: theme.palette.customColors.greyTextColor,
                    fontSize: COMMENT_FONT_SIZE,
                  }}
                >
                  This comment was deleted
                </CustomTypography>
              ) : (
                <>
                  {(isEditing ? draft : comment.body || "").trim().length >
                    0 && (
                    <CustomTypography
                      variant="heading8"
                      sx={{
                        whiteSpace: "pre-wrap",
                        lineHeight: 1.45,
                        color: theme.palette.text.primary,
                        wordBreak: "break-word",
                        fontSize: COMMENT_FONT_SIZE,
                        opacity: isEditing ? 0.55 : 1,
                      }}
                    >
                      {isEditing ? draft || comment.body : comment.body}
                    </CustomTypography>
                  )}
                  <CommentAttachmentsList
                    attachments={comment.attachments}
                    compact
                  />
                </>
              )}

              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={0.5}
                mt={0.5}
              >
                <CustomTypography
                  variant="heading8"
                  color={theme.palette.customColors.greyTextColor}
                  style={{
                    fontSize: COMMENT_META_FONT_SIZE,
                    fontWeight: 500,
                    lineHeight: 1.2,
                  }}
                >
                  {formatCommentTime(
                    isDeleted
                      ? comment.deleted_at || comment.updated_at
                      : comment.created_at,
                  )}
                </CustomTypography>
                {wasEdited && canViewHistory && (
                  <CustomTypography
                    variant="heading8"
                    onClick={() => void toggleHistory(comment)}
                    title={
                      historyExpanded
                        ? "Hide edit history"
                        : "Show edit history"
                    }
                    style={{
                      fontSize: COMMENT_META_FONT_SIZE,
                      fontWeight: 600,
                      lineHeight: 1.2,
                      color: historyExpanded
                        ? brand
                        : theme.palette.customColors.greyTextColor,
                      cursor: "pointer",
                      textDecoration: "underline",
                      textUnderlineOffset: 2,
                    }}
                  >
                    {historyExpanded ? "Hide edits" : "edited"}
                  </CustomTypography>
                )}
                {canViewHistory && isDeleted && (
                  <CustomTypography
                    variant="heading8"
                    onClick={() => void toggleHistory(comment)}
                    title={
                      historyExpanded
                        ? "Hide edit history"
                        : "Show edit history"
                    }
                    style={{
                      fontSize: COMMENT_META_FONT_SIZE,
                      fontWeight: 600,
                      lineHeight: 1.2,
                      color: historyExpanded
                        ? brand
                        : theme.palette.customColors.greyTextColor,
                      cursor: "pointer",
                      textDecoration: "underline",
                      textUnderlineOffset: 2,
                    }}
                  >
                    {historyExpanded ? "Hide history" : "View history"}
                  </CustomTypography>
                )}
              </Box>
            </Box>

            {canViewHistory && historyExpanded && (
              <Box
                mt={0.75}
                sx={{
                  backgroundColor: alpha(theme.palette.grey[500], 0.06),
                  borderRadius: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: "hidden",
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  px={1}
                  py={0.5}
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: alpha(brand, 0.06),
                  }}
                >
                  <CustomTypography
                    variant="heading8"
                    fontWeight={700}
                    style={{ fontSize: COMMENT_NAME_FONT_SIZE, color: brand }}
                  >
                    Edit history
                  </CustomTypography>
                  <Tooltip title="Close history">
                    <IconButton
                      size="small"
                      onClick={() => void toggleHistory(comment)}
                      aria-label="Close history"
                    >
                      <CloseRoundedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box p={1}>
                  {projectSourcingOpsStore.loadingProductCommentHistoryId ===
                  comment.id ? (
                    <Box display="flex" justifyContent="center" py={0.5}>
                      <CircularProgress size={16} />
                    </Box>
                  ) : (
                    renderHistoryRows(historyEntries)
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {isOwn && (
            <Avatar
              sx={{
                width: COMMENT_AVATAR_SIZE,
                height: COMMENT_AVATAR_SIZE,
                fontSize: "0.55rem",
                fontWeight: 600,
                bgcolor: isDeleted
                  ? theme.palette.grey[400]
                  : avatarColor(comment.author_name),
                flexShrink: 0,
              }}
            >
              {getInitials(comment.author_name)}
            </Avatar>
          )}
        </Box>
      );
    };

    return (
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        minHeight={0}
        sx={{ overflow: "hidden" }}
      >
        {!hideTitle && (
          <CustomTypography variant="heading5" sx={{ mb: 1.5 }}>
            Comments
          </CustomTypography>
        )}

        {archived && (
          <CustomTypography
            variant="heading8"
            color={theme.palette.customColors.greyTextColor}
            sx={{ mb: 1 }}
          >
            This project is archived. Comments are read-only.
          </CustomTypography>
        )}

        <Box
          flex={1}
          minHeight={0}
          sx={{
            overflowY: "auto",
            overflowX: "hidden",
            px: 0.5,
            py: 1,
            background: "transparent",
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
          }}
        >
          {projectSourcingOpsStore.loadingProductComments ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </Box>
          ) : flatComments.length === 0 ? (
            <Box py={4} textAlign="center">
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.greyTextColor}
                sx={{ fontSize: COMMENT_FONT_SIZE }}
              >
                No comments yet. Be the first to comment.
              </CustomTypography>
            </Box>
          ) : (
            flatComments.map((comment) => renderMessage(comment))
          )}
          <div ref={listEndRef} />
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            pt: 1,
            pb: 0.5,
            backgroundColor: "transparent",
          }}
        >
          {editingComment && (
            <Box
              mb={1}
              sx={{
                display: "flex",
                alignItems: "stretch",
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: alpha(brand, 0.06),
                border: `1px solid ${alpha(brand, 0.15)}`,
              }}
            >
              <Box sx={{ width: 3.5, backgroundColor: brand, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0, px: 1.25, py: 0.75 }}>
                <CustomTypography
                  variant="heading8"
                  fontWeight={700}
                  sx={{ color: brand, fontSize: COMMENT_NAME_FONT_SIZE }}
                >
                  Editing message
                </CustomTypography>
                <CustomTypography
                  variant="heading8"
                  color={theme.palette.customColors.greyTextColor}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    fontSize: COMMENT_FONT_SIZE,
                  }}
                >
                  {getQuotePreview(editingComment)}
                </CustomTypography>
              </Box>
              <IconButton
                size="small"
                onClick={cancelEdit}
                sx={{ alignSelf: "center", mr: 0.5 }}
              >
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          )}

          {replyingTo && !editingComment && (
            <Box
              mb={1}
              sx={{
                display: "flex",
                alignItems: "stretch",
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: alpha(brand, 0.06),
                border: `1px solid ${alpha(brand, 0.15)}`,
              }}
            >
              <Box sx={{ width: 3.5, backgroundColor: brand, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0, px: 1.25, py: 0.75 }}>
                <CustomTypography
                  variant="heading8"
                  fontWeight={700}
                  sx={{ color: brand, fontSize: COMMENT_NAME_FONT_SIZE }}
                >
                  Replying to {replyingTo.author_name || "comment"}
                </CustomTypography>
                <CustomTypography
                  variant="heading8"
                  color={theme.palette.customColors.greyTextColor}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    fontSize: COMMENT_FONT_SIZE,
                    fontStyle: replyingTo.deleted_at ? "italic" : "normal",
                  }}
                >
                  {getQuotePreview(replyingTo)}
                </CustomTypography>
              </Box>
              <IconButton
                size="small"
                onClick={cancelReply}
                sx={{ alignSelf: "center", mr: 0.5 }}
              >
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          )}

          {composerError && (
            <CustomTypography
              variant="heading8"
              color={theme.palette.customColors.pinkDark || "#AF095E"}
              style={{
                fontSize: COMMENT_FONT_SIZE,
                marginBottom: 8,
                lineHeight: 1.35,
              }}
            >
              {composerError}
            </CustomTypography>
          )}

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 1,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
              px: 1.25,
              py: 0.75,
              backgroundColor: theme.palette.background.paper,
              "&:focus-within": {
                borderColor: brand,
                boxShadow: `0 0 0 3px ${alpha(brand, 0.12)}`,
              },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FormSmallTextField
                multiline
                minRows={1}
                maxRows={8}
                variant="standard"
                placeholder={
                  archived
                    ? "Comments are disabled for archived projects"
                    : editingComment
                      ? "Edit your message..."
                      : replyingTo
                        ? "Type a reply..."
                        : "Type a message..."
                }
                value={draft}
                onChange={(_id, value) =>
                  setDraft((value || "").slice(0, COMMENT_MAX_LENGTH))
                }
                disabled={!canType}
                maxLength={COMMENT_MAX_LENGTH}
                marginBottom={0}
                fontSize="12.5px"
                inputRef={draftInputRef}
                onKeyDown={(event) => {
                  if (event.key === "Escape" && (editingId || replyingToId)) {
                    event.preventDefault();
                    if (editingId) cancelEdit();
                    else cancelReply();
                    return;
                  }
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void handleSend();
                  }
                }}
                sx={{
                  "& .MuiInput-root:before": { borderBottom: "none" },
                  "& .MuiInput-root:after": { borderBottom: "none" },
                  "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                    borderBottom: "none",
                  },
                  "& .MuiInputBase-root": {
                    padding: 0,
                    alignItems: "flex-end",
                  },
                  "& textarea": {
                    resize: "none",
                    overflow: "auto !important",
                    lineHeight: 1.4,
                  },
                }}
              />

              {pendingFiles.length > 0 && !editingComment && (
                <Box
                  sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}
                >
                  {pendingFiles.map((file, index) => (
                    <Chip
                      key={`${file.name}-${index}`}
                      label={`${file.name} (${formatBytes(file.size)})`}
                      size="small"
                      onDelete={
                        isBusy
                          ? undefined
                          : () =>
                              setPendingFiles((prev) =>
                                prev.filter((_, i) => i !== index),
                              )
                      }
                      deleteIcon={<CloseRoundedIcon />}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexShrink: 0,
                alignSelf: "flex-end",
                pb: 0.25,
              }}
            >
              {!editingComment && (
                <Tooltip title="Attach files">
                  <span style={{ display: "inline-flex" }}>
                    <IconButton
                      disabled={!canType || isBusy}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        width: 36,
                        height: 36,
                        color: theme.palette.customColors.greyTextColor,
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              <Tooltip
                title={
                  canSend
                    ? editingComment
                      ? "Save edit"
                      : replyingTo
                        ? "Send reply"
                        : pendingFiles.length > 0 && !draft.trim()
                          ? "Send attachment"
                          : "Send"
                    : pendingFiles.length > 0
                      ? "Total attachment size must be at most 5MB"
                      : "Write a message or attach a file"
                }
              >
                <span style={{ display: "inline-flex" }}>
                  <IconButton
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => void handleSend()}
                    disabled={!canSend}
                    sx={{
                      width: 36,
                      height: 36,
                      backgroundColor: canSend
                        ? brand
                        : theme.palette.action.disabledBackground,
                      color: canSend
                        ? theme.palette.common.white
                        : theme.palette.action.disabled,
                      "&:hover": {
                        backgroundColor: canSend
                          ? theme.palette.primary.dark
                          : theme.palette.action.disabledBackground,
                      },
                    }}
                  >
                    {isBusy ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SendRoundedIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            onChange={handleFilesSelected}
          />
        </Box>

        <DeleteConfirmation
          id={deleteTargetId ?? 0}
          open={deleteTargetId !== null}
          title="Delete comment"
          content="Are you sure you want to delete this comment? It will be hidden for everyone."
          onDeleteConfirm={(id) => void handleConfirmDelete(id)}
          onCancel={() => setDeleteTargetId(null)}
        />
      </Box>
    );
  },
);

export default ProjectProductComments;
